import type { AudioLayer, LayerFrame, MediaLayer, TextLayer, TextLayerStyle, VideoLayer } from './types';
import { SPEED_MIN, SPEED_MAX, DEFAULT_TEXT_STYLE } from './layerTypeConfig';
import { clampLayerFrame } from './clampLayerFrame';

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const num = (v: unknown, fallback: number, lo: number, hi: number) =>
  typeof v === 'number' && Number.isFinite(v) ? clamp(v, lo, hi) : fallback;

/** Fill/clamp the text-style fields that postdate the original schema
 *  (`clip.data.editorLayers` is schemaless JSON, so old rows lack them). */
function normalizeTextStyle(raw: Partial<TextLayerStyle> | undefined): TextLayerStyle {
  const s = { ...DEFAULT_TEXT_STYLE, ...(raw ?? {}) };
  return {
    ...s,
    fontUrl: typeof s.fontUrl === 'string' && s.fontUrl ? s.fontUrl : null,
    backgroundOpacity: num(s.backgroundOpacity, 1, 0, 1),
    borderRadius: num(s.borderRadius, 0, 0, 1000),
    paddingX: num(s.paddingX, 0, 0, 1000),
    paddingY: num(s.paddingY, 0, 0, 1000),
  };
}

/** Fill/clamp fields on persisted layers that may predate them
 *  (clip.data.editorLayers is schemaless JSON):
 *  - video/audio: speed & trim. Video also gets `source` defaulted to 'upload'
 *    (legacy rows predate the field) and a persisted `frame` re-clamped in case
 *    it was written out of range.
 *  - text: the style fields added after the original schema (fontUrl,
 *    backgroundOpacity, borderRadius, paddingX/Y).
 *  Image layers pass through by reference. */
export function normalizeLayer(layer: MediaLayer): MediaLayer {
  if (layer.type === 'motion') {
    const rect = layer.rect;
    return {
      ...layer,
      rect: {
        x: num(rect?.x, 0, -10, 10), y: num(rect?.y, 0, -10, 10),
        w: num(rect?.w, 1, 0.001, 20), h: num(rect?.h, 1, 0.001, 20),
      },
      rotation: num(layer.rotation, 0, -3600, 3600),
      opacity: num(layer.opacity, 1, 0, 1),
    };
  }
  if (layer.type === 'text') {
    const text = layer as TextLayer;
    return { ...text, style: normalizeTextStyle(text.style) };
  }
  if (layer.type !== 'video' && layer.type !== 'audio') return layer;
  const raw = layer as MediaLayer & {
    mediaDuration?: number; speed?: number; trimStart?: number; trimEnd?: number;
    source?: string; frame?: LayerFrame; sfxId?: unknown;
  };
  const mediaDuration =
    typeof raw.mediaDuration === 'number' && raw.mediaDuration > 0
      ? raw.mediaDuration
      : Math.max(0.1, layer.end - layer.start);
  const speed =
    typeof raw.speed === 'number' && raw.speed > 0
      ? clamp(raw.speed, SPEED_MIN, SPEED_MAX)
      : 1;
  const trimStart =
    typeof raw.trimStart === 'number' ? clamp(raw.trimStart, 0, mediaDuration) : 0;
  const trimEndRaw =
    typeof raw.trimEnd === 'number' && raw.trimEnd > 0
      ? clamp(raw.trimEnd, trimStart, mediaDuration)
      : mediaDuration;
  // Preserve the invariant end - start === (trimEnd - trimStart) / speed even
  // when the persisted media outlasts the clip window (e.g. legacy rows, or
  // rows written before this cap existed at creation time).
  const trimEnd = Math.min(trimEndRaw, trimStart + (layer.end - layer.start) * speed);
  const result: MediaLayer = { ...layer, mediaDuration, speed, trimStart, trimEnd };
  if (layer.type === 'audio') {
    // A sound effect's catalog id; junk from old rows is dropped.
    if (typeof raw.sfxId === 'string' && raw.sfxId) (result as AudioLayer).sfxId = raw.sfxId;
    else delete (result as { sfxId?: unknown }).sfxId;
  }
  if (layer.type === 'video') {
    (result as VideoLayer).source = raw.source === 'clip' ? 'clip' : 'upload';
    if (raw.frame && typeof raw.frame === 'object') {
      (result as VideoLayer).frame = clampLayerFrame(raw.frame);
    }
  }
  return result;
}
